<template>
  <v-card class="page__colors overflow-hidden" flat>
    <v-row no-gutters class="py-0 px-2">
      <v-col cols="12" class="px-0">
        <a type="primary" size="xsmall" @click="beforeLeave">
          <v-icon size="12" class="backIcon">mdi-arrow-left</v-icon>
          {{ $t('message.actions.return') }}
        </a>
      </v-col>
      <v-col cols="12">
        <span
          class="body-bold-medium page__colors--title"
          style="vertical-align: middle"
        >
          {{ $t('message.pages.video.colors') }}
        </span>
      </v-col>
    </v-row>

    <v-row no-gutters class="py-0 px-2 mb-2">
      <v-col cols="12">
        <v-card flat>
          <span class="body-regular-smaller page__colors--subtext">
            {{ $t('message.pages.video.messageAppearance') }}
            <span
              class="cursor-pointer body-bold-smaller page__colors--article"
              @click="openHelpDesk"
              >{{ $t('message.actions.knowMore') }}
              <v-icon size="11px" class="page__colors--article-icon"
                >mdi-open-in-new</v-icon
              ></span
            >
          </span>
        </v-card>
      </v-col>

      <v-row class="pt-4 pb-2 mb-2">
        <v-col cols="12">
          <span class="body-bold-base page__colors--player-text">
            {{ $t('message.playerPage.playerColors') }}
          </span>
        </v-col>
      </v-row>

      <BaseActionList
        :title="$t('message.playerPage.primaryColor')"
        :subtitle="$t('message.playerPage.primaryColorDesc')"
      >
        <template #secondary-action>
          <v-menu
            :disabled="!hasEditVideoPerm"
            offset-x
            v-model="menuPrimaryColor"
            top
            nudge-bottom="105"
            nudge-left="16"
            :close-on-content-click="false"
          >
            <template v-slot:activator="{ on }">
              <v-btn
                :color="primaryColor"
                :style="setPrimaryColor"
                v-on="on"
                icon
                class="rounded-pill border-slate"
              >
                <span class="describeHexa">{{ primaryColor }}</span></v-btn
              >
            </template>
            <v-color-picker
              elevation="3"
              v-model="primaryColor"
              flat
              mode="hexa"
              hide-mode-switch
              :maxlength="max"
              @input="changePlayer()"
            ></v-color-picker>
          </v-menu>
        </template>
      </BaseActionList>

      <BaseActionList
        :title="$t('message.playerPage.secondaryColor')"
        :subtitle="$t('message.playerPage.secondaryColorDesc')"
      >
        <template #secondary-action>
          <v-menu
            :disabled="!hasEditVideoPerm"
            offset-x
            v-model="menuControlsColor"
            top
            nudge-bottom="105"
            nudge-left="16"
            :close-on-content-click="false"
          >
            <template v-slot:activator="{ on }">
              <v-btn
                :color="controlsColor"
                v-on="on"
                :style="setControlsColor"
                icon
                class="rounded-pill border-slate"
                ><span class="describeHexa">{{ controlsColor }}</span></v-btn
              >
            </template>
            <v-color-picker
              elevation="3"
              v-model="controlsColor"
              flat
              mode="hexa"
              hide-mode-switch
              :maxlength="max"
              @input="changePlayer()"
            ></v-color-picker>
          </v-menu>
        </template>
      </BaseActionList>
    </v-row>

    <span
      v-if="internalError"
      class="red--text caption align-text ml-2 mt-2 d-block"
    >
      <v-icon color="red" size="16" class="mt-n1"> mdi-alert-circle </v-icon>
      {{ $t('message.pages.video.errors.addColors') }}
    </span>

    <portal to="actions-footer">
      <ActionModal
        :showAction1="hasEditVideoPerm"
        :showAction2="hasEditVideoPerm"
        :disableTooltip1="!withoutChanges"
        :disableTooltip2="!withoutChanges"
        :disabledAction1="withoutChanges"
        :disabledAction2="withoutChanges"
        :handleAction1="handleUndoChanges"
        :handleAction2="handleUpdateColors"
        :loadingAction2="loadingButtonSave"
      />
    </portal>
    <unsaved-changes-modal />
  </v-card>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import BaseActionList from '@/components/BaseActionList'
import i18n from '@/translations/lang'
import linkHelper from '@/utils/linkHelper.mixin.js'
import _ from 'lodash'
import UnsavedChangesModal from '@/components/UnsavedChangesModal.vue'
import ActionModal from '@/components/ActionModal.vue'

export default {
  name: 'VideoPlayerColors',
  components: {
    UnsavedChangesModal,
    BaseActionList,
    ActionModal,
  },
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
    liveVideoId: {
      required: false,
    },
    isExternalSourceVideo: {
      type: Boolean,
      default: false,
    },
    hasEdit: {
      type: Array,
    },
  },
  mixins: [linkHelper],
  data: () => ({
    menuPrimaryColor: false,
    menuControlsColor: false,
    controlsColor: null,
    primaryColor: null,
    max: 7,
    loadingButtonSave: false,
    videoPlayer: {},
    profileId: null,
    profileConfig: {},
    initialConfig: {
      controlsColor: null,
      primaryColor: null,
    },
    withoutChanges: true,
    videoId: '',
  }),
  async mounted() {
    this.videoId = this.liveVideoId ? this.liveVideoId : this.$route.params.id
    this.profileId = this.$store.state.video.video.config?.profile
    this.profileConfig = this.$store.state.video.profile?.colors
    this.videoPlayer = this.$store.state.video.player.config
    this.controlsColor = this.profileConfig?.controlsColor
    this.primaryColor = this.profileConfig?.primaryColor
    this.initialConfig = _.cloneDeep(this.profile.colors)
    this.hasEdit.push(this.checkChanges)

    this.$root.$on('discardChanges', () => {
      this.handleUndoChanges()
      this.handleBack()
    })
  },
  watch: {
    checkChanges: {
      handler(hasChanges) {
        this.withoutChanges = !hasChanges
      },
    },
  },
  computed: {
    ...mapState({
      profile: (state) => state.video.profile,
      video: (state) => state.video.video,
      profile_id: (state) => state.video.profile_id,
      internalError: (state) => state.video.internalError,
    }),
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
    setPrimaryColor() {
      const { menuPrimaryColor, primaryColor } = this
      let root = document.querySelector(':root')
      root.style.setProperty('--plyr-color-main', primaryColor)
      return {
        backgroundColor: primaryColor,
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        borderRadius: menuPrimaryColor ? '50%' : '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    setControlsColor() {
      const { controlsColor, menuControlsColor } = this
      let root = document.querySelector(':root')
      root.style.setProperty('--plyr-video-control-color', controlsColor)
      return {
        backgroundColor: controlsColor,
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        borderRadius: menuControlsColor ? '50%' : '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    checkChanges() {
      const laterPrimaryColor = this.profile.colors.primaryColor
      const laterControlsColor = this.profile.colors.controlsColor

      const upcomingPrimaryColor = this.primaryColor
      const upcomingControlsColor = this.controlsColor

      const isPrimaryColorChanged = laterPrimaryColor !== upcomingPrimaryColor
      const isControlsColorChanged =
        laterControlsColor !== upcomingControlsColor

      return isPrimaryColorChanged || isControlsColorChanged
    },
  },
  methods: {
    ...mapActions('video', [
      'updateProfile',

      'createProfile',

      'setProfileToVideos',
    ]),
    ...mapMutations('video', [
      'UPDATE_VIDEO_PROFILE',
      'UPDATE_VIDEO_PROFILE_ID',
      'UPDATE_STATUS_ERROR',
    ]),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    openHelpDesk() {
      const link = this.getCustomizeColors()
      window.open(link, '_blank')
    },
    async handleUpdateColors() {
      this.loadingButtonSave = true
      try {
        this.profile.colors.controlsColor = this.controlsColor
        this.profile.colors.primaryColor = this.primaryColor
        if (!this.video.config.profile) {
          await this.createProfile(this.videoId)
          this.video.config.profile = this.profile_id
          this.profileId = this.profile_id
          await this.setProfileToVideos([this.profileId, this.videoId])
        }
        await this.updateProfile([this.profileId, this.profile])
        this.handleSnackbar(i18n.t('message.pages.video.changesSaved'))
        this.initialConfig = _.cloneDeep(this.profile.colors)
        this.withoutChanges = true
        this.loadingButtonSave = false
      } catch (e) {
        console.error(e)
        this.loadingButtonSave = false
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    async changePlayer() {
      if (!this.isExternalSourceVideo) {
        const iframe = document.getElementById('panda-player')
        if (iframe) {
          iframe.contentWindow.postMessage(
            {
              type: 'colors',
              parameter: {
                colors: {
                  primaryColor: this.primaryColor,
                  controlsColor: this.controlsColor,
                },
              },
            },
            '*'
          )
        }
      }
      this.UPDATE_STATUS_ERROR(false)
      return
    },
    handleUndoChanges() {
      this.primaryColor = _.cloneDeep(this.initialConfig.primaryColor)
      this.controlsColor = _.cloneDeep(this.initialConfig.controlsColor)
      this.changePlayer()
    },
    beforeLeave() {
      if (this.checkChanges) {
        this.$root.$emit('unsavedChangesModal')
      } else {
        this.UPDATE_STATUS_ERROR(false)
        this.handleBack()
      }
    },
    handleSnackbar(item) {
      if (!item) {
        item = i18n.t('message.changesSaved')
      }
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: item,
        type: 'success',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.border-slate {
  height: 16px !important;
  width: 17px !important;
  border: varFigma(neutral-30-base) 1px solid;
}
.describeHexa {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 10%;
  color: varFigma(neutral-40-light);
  margin-right: 130px;
  width: 100px;
  display: flex;
  justify-content: end;
}

.page__colors {
  &--back,
  &--back-icon {
    color: varFigma(brand-60-base);
  }

  &--title,
  &--player-text {
    color: varFigma(neutral-20-dark);
  }

  &--article,
  &--article-icon {
    color: varFigma(neutral-30-base);
  }

  &--subtext,
  &--desc-feature {
    color: varFigma(neutral-40-light);
  }
}
</style>
